import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExtensionIcon from '@mui/icons-material/Extension';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { Phase, PhaseStatus } from '../../types';
import { adminPhaseApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { Can } from '../../auth/Can';
import { ConfirmDialog } from './ConfirmDialog';
import { PhaseContractTagsDialog } from './PhaseContractTagsDialog';
import { PHASE_STATUS_META, PHASE_STATUS_OPTIONS } from './statusMeta';

interface PhaseFormState {
  open: boolean;
  mode: 'create' | 'edit';
  phase: Phase | null;
  name: string;
  description: string;
  status: PhaseStatus;
  orderIndex: string;
  saving: boolean;
}

const emptyForm = (nextOrder: number): PhaseFormState => ({
  open: false,
  mode: 'create',
  phase: null,
  name: '',
  description: '',
  status: 'DRAFT',
  orderIndex: String(nextOrder),
  saving: false,
});

export function PhaseManagement(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<PhaseFormState>(emptyForm(0));
  const [deleteTarget, setDeleteTarget] = useState<Phase | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [tagsPhase, setTagsPhase] = useState<Phase | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { items: list } = await adminPhaseApi.listByProject(projectId, { limit: 200 });
      setPhases([...list].sort((a, b) => a.order_index - b.order_index));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách giai đoạn'), 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => setForm({ ...emptyForm(phases.length), open: true, mode: 'create' });

  const openEdit = (phase: Phase) => {
    setForm({
      open: true,
      mode: 'edit',
      phase,
      name: phase.name,
      description: phase.description ?? '',
      status: phase.status,
      orderIndex: String(phase.order_index),
      saving: false,
    });
  };

  const closeForm = () => setForm(emptyForm(phases.length));

  const handleSubmitForm = async () => {
    if (!projectId) return;
    if (!form.name.trim()) {
      notify('Vui lòng nhập tên giai đoạn', 'warning');
      return;
    }
    const orderIndex = Number(form.orderIndex);
    if (Number.isNaN(orderIndex)) {
      notify('Thứ tự phải là số', 'warning');
      return;
    }
    setForm((prev) => ({ ...prev, saving: true }));
    try {
      if (form.mode === 'create') {
        await adminPhaseApi.create(projectId, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          order_index: orderIndex,
          status: form.status,
        });
        notify('Đã tạo giai đoạn mới', 'success');
      } else if (form.phase) {
        await adminPhaseApi.update(form.phase.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          order_index: orderIndex,
          status: form.status,
        });
        notify('Đã cập nhật giai đoạn', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu giai đoạn'), 'error');
      setForm((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminPhaseApi.remove(deleteTarget.id);
      notify('Đã xóa mềm giai đoạn', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa giai đoạn'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const swapOrder = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= phases.length) return;
    const current = phases[index];
    const target = phases[targetIndex];
    setReorderingId(current.id);
    try {
      await Promise.all([
        adminPhaseApi.update(current.id, { order_index: target.order_index }),
        adminPhaseApi.update(target.id, { order_index: current.order_index }),
      ]);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thay đổi thứ tự'), 'error');
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" underline="hover" onClick={() => navigate('/admin/projects')}>
          Dự án
        </Link>
        <Typography color="text.primary">Giai đoạn</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Quản lý giai đoạn
        </Typography>
        <Can permission="PHASE_CREATE">
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Thêm giai đoạn
          </Button>
        </Can>
      </Stack>

      <Can
        permission="PHASE_VIEW"
        fallback={<Alert severity="warning">Bạn không đủ quyền xem danh sách giai đoạn.</Alert>}
      >
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Thứ tự</TableCell>
                  <TableCell>Tên giai đoạn</TableCell>
                  <TableCell>Mô tả</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell align="right">Hành động</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && phases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Chưa có giai đoạn nào.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  phases.map((phase, index) => (
                    <TableRow key={phase.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2">{phase.order_index}</Typography>
                          <IconButton
                            size="small"
                            disabled={index === 0 || reorderingId !== null}
                            onClick={() => void swapOrder(index, -1)}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={index === phases.length - 1 || reorderingId !== null}
                            onClick={() => void swapOrder(index, 1)}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>{phase.name}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>{phase.description || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={PHASE_STATUS_META[phase.status].label}
                          color={PHASE_STATUS_META[phase.status].color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Quản lý tính năng">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/projects/${projectId}/phases/${phase.id}`)}
                          >
                            <ExtensionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Can permission="PHASE_UPDATE">
                          <Tooltip title="Quản lý loại hợp đồng">
                            <IconButton size="small" onClick={() => setTagsPhase(phase)}>
                              <LocalOfferIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can permission="PHASE_UPDATE">
                          <Tooltip title="Sửa">
                            <IconButton size="small" onClick={() => openEdit(phase)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can permission="PHASE_DELETE">
                          <Tooltip title="Xóa mềm">
                            <IconButton size="small" onClick={() => setDeleteTarget(phase)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Can>

      <Dialog open={form.open} onClose={form.saving ? undefined : closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{form.mode === 'create' ? 'Thêm giai đoạn' : 'Sửa giai đoạn'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tên giai đoạn"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              disabled={form.saving}
            />
            <TextField
              label="Mô tả"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              disabled={form.saving}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Thứ tự"
                type="number"
                value={form.orderIndex}
                onChange={(e) => setForm((prev) => ({ ...prev, orderIndex: e.target.value }))}
                disabled={form.saving}
                sx={{ width: 140 }}
              />
              <FormControl fullWidth disabled={form.saving}>
                <InputLabel id="phase-status-label">Trạng thái</InputLabel>
                <Select
                  labelId="phase-status-label"
                  label="Trạng thái"
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as PhaseStatus }))}
                >
                  {PHASE_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {PHASE_STATUS_META[status].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={form.saving}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleSubmitForm()}
            variant="contained"
            disabled={form.saving}
            startIcon={form.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {form.mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      <PhaseContractTagsDialog phase={tagsPhase} onClose={() => setTagsPhase(null)} />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm giai đoạn"
        message={`Giai đoạn "${deleteTarget?.name}" sẽ bị ẩn khỏi hệ thống (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
